import {useQuery} from '@tanstack/react-query';
import {
  countPiecesInFen,
  TABLEBASE_MAX_PIECES,
  TablebaseCategory,
  TablebaseMove,
} from '@/Screens/Study/utils/explorerUtils';
import {ExplorerError} from './useOpeningExplorer';
import Environment from '../../../environment';

/**
 * Syzygy 7-man endgame tablebase. Only queried when the position has at
 * most 7 men — the caller doesn't need to gate it.
 *
 * Split across two backends by piece count, matching what we actually have
 * data for (see OPENING_TABLEBASE_SELFHOST_DESIGN.md):
 *  - 0-5 pieces: our own self-hosted lila-tablebase (TABLEBASE_BASE_URL in
 *    env.local.js/env.production.js), loaded with 3-4-5-piece Syzygy WDL+DTZ
 *    tables. 0-2 piece positions (e.g. bare K v K) resolve via chess rules
 *    directly (insufficient material), no table files needed for those.
 *  - 6-7 pieces: still Lichess's public tablebase.lichess.ovh - self-hosting
 *    6-7 piece tables was explicitly ruled out (far larger, hundreds of GB+).
 *    Falls back automatically if TABLEBASE_BASE_URL isn't set at all.
 *
 * Unlike the opening explorer, tablebase.lichess.ovh is a public, unauthenticated
 * endpoint - AND its CORS preflight response does not list `Authorization` in
 * Access-Control-Allow-Headers (explorer.lichess.ovh's does). Sending the
 * explorer's Bearer token here doesn't just go unused: it makes the browser's
 * preflight fail and blocks every request outright (fetch throws, surfaced
 * here as an ExplorerError('network', ...)), which is why this used to show
 * "Tablebase is busy" permanently even though the position was reachable
 * fine outside the browser (e.g. via curl). Do not add auth headers here -
 * our self-hosted instance is also unauthenticated, so this applies to both.
 */

// Ceiling of what we actually loaded onto the self-hosted instance (3-4-5
// piece tables). Positions above this still fall inside TABLEBASE_MAX_PIECES
// (7) but route to Lichess's public service instead.
const SELF_HOST_MAX_PIECES = 5;

function tablebaseUrl(fen: string, pieceCount: number): string {
  const selfHostBase = (Environment as any).TABLEBASE_BASE_URL;
  const base =
    selfHostBase && pieceCount <= SELF_HOST_MAX_PIECES
      ? selfHostBase
      : 'https://tablebase.lichess.ovh';
  return `${base}/standard?fen=${encodeURIComponent(fen)}`;
}

export type TablebaseData = {
  category: TablebaseCategory;
  dtz: number | null;
  dtm: number | null;
  checkmate: boolean;
  stalemate: boolean;
  insufficient_material: boolean;
  moves: TablebaseMove[];
};

export default function useTablebase(
  fen: string | undefined | null,
  enabled = true,
) {
  const pieceCount = fen ? countPiecesInFen(fen) : 0;
  const withinTablebase = !!fen && pieceCount <= TABLEBASE_MAX_PIECES;

  return useQuery<TablebaseData, ExplorerError>(
    ['tablebase', fen],
    async ({signal}) => {
      let res: Response;
      try {
        res = await fetch(tablebaseUrl(fen as string, pieceCount), {signal});
      } catch (networkErr) {
        throw new ExplorerError('network', undefined, String(networkErr));
      }
      if (!res.ok) {
        if (res.status === 401 || res.status === 403) {
          throw new ExplorerError('auth', res.status);
        }
        if (res.status === 429) {
          throw new ExplorerError('rate-limit', 429);
        }
        throw new ExplorerError('server', res.status);
      }
      return res.json();
    },
    {
      enabled: enabled && withinTablebase,
      staleTime: Infinity,
      keepPreviousData: true,
      retry: (failureCount, error: unknown) => {
        if (error instanceof ExplorerError) {
          if (error.kind === 'auth') {
            return false;
          }
          if (error.kind === 'rate-limit') {
            return failureCount < 2;
          }
          return failureCount < 2;
        }
        return false;
      },
      retryDelay: (_, error: unknown) => {
        if (error instanceof ExplorerError && error.kind === 'rate-limit') {
          return 61_000;
        }
        return 1_000;
      },
    },
  );
}
